import {
  LinkButton,
  Dropdown,
  Panel,
  DropdownMenuButton,
  DropdownMenuSplitter,
} from '@epam/promo';
import { IDropdownToggler } from '@epam/uui-core';
import { ReactComponent as SelectedIcon } from '@epam/assets/icons/common/notification-done-18.svg';
import { DropdownBodyProps } from '@epam/uui-components';
import { USER_ROLE, UserRole } from 'models/user.model';
import { useAdminDeleteQuery, useModal, useUpdateAdminQuery } from 'hooks';
import css from './PermissionControl.module.scss';
import { PermissionControlProps } from './PermissionControl.model';

export const PermissionControl = ({ user }: PermissionControlProps) => {
  const modal = useModal();
  const { mutate: updateAdminRole } = useUpdateAdminQuery();
  const { mutate: deleteAdmin } = useAdminDeleteQuery();
  const roles = Object.keys(USER_ROLE)
    .filter((role) => role !== 'GUEST')
    .map((role) => ({
      id: role,
      value: USER_ROLE[role as UserRole],
    }));

  const renderTarget = ({
    isOpen,
    isDropdown,
    toggleDropdownOpening,
    isInteractedOutside,
    ref,
    onClick,
  }: IDropdownToggler) => (
    <LinkButton
      ref={ref}
      caption={USER_ROLE[user.role]}
      cx={css['permission-control__target']}
      isDropdown={isDropdown}
      isInteractedOutside={isInteractedOutside}
      isOpen={isOpen}
      size="36"
      toggleDropdownOpening={toggleDropdownOpening}
      onClick={onClick}
    />
  );

  const renderRoles = ({ onClose }: DropdownBodyProps) => (
    <Panel background="white" cx={css['permission-control__roles']}>
      {roles.map((role) => (
        <DropdownMenuButton
          key={role.id}
          caption={role.value}
          cx={
            role.id === user.role
              ? css['permission-control__text']
              : css['permission-control__text-border']
          }
          icon={role.id === user.role ? SelectedIcon : undefined}
          iconPosition="right"
          isDisabled={role.id === user.role}
          onClick={() =>
            modal.confirm({
              text:
                user.status !== 'ACTIVE'
                  ? `Are you sure you want to change the user role for ${user.email}?`
                  : `Are you sure you want to change the user role for ${user.firstName} ${user.lastName}`,
              highlight:
                user.status !== 'ACTIVE'
                  ? user.email
                  : `${user.firstName} ${user.lastName}`,
              onSuccess: () => {
                updateAdminRole({ id: user.id, role: role.id as UserRole });
                onClose();
              },
            })
          }
        />
      ))}
    </Panel>
  );

  const renderBody = (props: DropdownBodyProps) => (
    <Panel shadow background="white" cx={css['permission-control ']}>
      {renderRoles(props)}
      <DropdownMenuSplitter cx={css['permission-control__splitter']} />
      <Panel background="white" cx={css['permission-control__delete']}>
        <DropdownMenuButton
          caption="Delete"
          cx={css['permission-control__text']}
          onClick={() =>
            modal.delete({
              text:
                user.status !== 'ACTIVE'
                  ? `Are you sure you want to delete ${user.email}?`
                  : `Are you sure you want to revoke permissions of ${user.firstName} ${user.lastName}?`,
              highlight:
                user.status !== 'ACTIVE'
                  ? user.email
                  : `${user.firstName} ${user.lastName}`,
              onSuccess: () => deleteAdmin({ id: user.id }),
            })
          }
        />
      </Panel>
    </Panel>
  );

  return (
    <Dropdown
      placement="bottom-end"
      renderBody={renderBody}
      renderTarget={renderTarget}
    />
  );
};
